import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, FileText, AlertCircle, Check, UserPlus, Sparkles } from 'lucide-react';
import { apiPersons } from '../api/client.js';

const SKIP_LABELS = {
  'email-existant': 'Déjà présent (email)',
  'smileId-existant': 'Déjà présent (smileId)',
  'rien-a-completer': 'Déjà présent — rien à compléter',
  'doublon-csv': 'Doublon dans le CSV',
  'sans-nom': 'Ligne sans nom',
};

const PENTA_LABELS = {
  smileId: 'Smile ID',
  smilePenta: 'Gescom Penta',
  pentaCrmId: 'Penta CRM ID',
  codeCollGescom: 'Code coll. Gescom',
  bu: 'BU',
};

/**
 * Import CSV GESCOM 430 (collaborateurs) — création seulement.
 *
 * Deux temps : un premier upload calcule un PLAN (dry-run, aucune écriture) que
 * l'utilisateur prévisualise (créations / ignorés), puis « Appliquer » exécute
 * réellement les créations + rattachements hiérarchiques côté backend.
 */
export default function ImportCollaborateursModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrichExisting, setEnrichExisting] = useState(false);
  const [plan, setPlan] = useState(null); // résultat dry-run
  const [result, setResult] = useState(null); // résultat appliqué
  const [error, setError] = useState(null);
  // Sélection des lignes à appliquer (cases à cocher). Sets de clés.
  const [selCreate, setSelCreate] = useState(() => new Set());
  const [selEnrich, setSelEnrich] = useState(() => new Set());

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // À chaque nouveau plan : tout sélectionner par défaut.
  useEffect(() => {
    if (!plan) return;
    setSelCreate(new Set((plan.toCreate ?? []).map((p) => p.key)));
    setSelEnrich(new Set((plan.toEnrich ?? []).map((e) => e.personId)));
  }, [plan]);

  const toggle = (setFn) => (id) =>
    setFn((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleCreate = toggle(setSelCreate);
  const toggleEnrich = toggle(setSelEnrich);

  const pickFile = async (f) => {
    setError(null);
    setPlan(null);
    setResult(null);
    setFile(f);
    setCsv(f ? await f.text() : null);
  };

  const preview = async () => {
    if (!csv) return;
    setBusy(true);
    setError(null);
    try {
      setPlan(await apiPersons.importGescom(csv, { apply: false, enrichExisting }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!csv) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiPersons.importGescom(csv, {
        apply: true,
        enrichExisting,
        selectedCreateKeys: [...selCreate],
        selectedEnrichIds: [...selEnrich],
      });
      setResult(r);
      onImported?.(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const view = result ?? plan;

  return createPortal(
    <div style={backdropStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <header style={headerStyle}>
          <strong style={{ color: 'var(--primary)' }}>Importer des collaborateurs (GESCOM)</strong>
          <button onClick={onClose} style={iconBtn} title="Fermer">
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: '1.25rem' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: '1rem' }}>
            Export GESCOM 430 (CSV). L'import <strong>crée uniquement</strong> les collaborateurs
            absents (rapprochés par email puis smileId) — les fiches existantes ne sont jamais
            modifiées. La hiérarchie (responsable) est rattachée automatiquement.
          </p>

          <label style={dropZoneStyle}>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              style={{ display: 'none' }}
            />
            <FileText size={28} color="var(--text-muted)" />
            <div style={{ marginTop: '0.5rem' }}>
              {file ? (
                <span>
                  <strong>{file.name}</strong> ({Math.round(file.size / 1024)} Ko)
                </span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>Cliquer pour choisir un CSV…</span>
              )}
            </div>
          </label>

          {!result && (
            <button
              type="button"
              onClick={() => {
                setEnrichExisting((v) => !v);
                setPlan(null); // force une nouvelle prévisualisation
              }}
              style={{
                ...pillStyle,
                marginTop: '0.85rem',
                background: enrichExisting ? 'var(--accent)' : 'var(--glass)',
                color: enrichExisting ? '#fff' : 'var(--text)',
                borderColor: enrichExisting ? 'var(--accent)' : 'var(--glass-border)',
              }}
              title="Complète les champs Penta/CRM vides des fiches déjà existantes (non destructif)"
            >
              <Sparkles size={14} />
              Compléter le référentiel Penta des fiches existantes
            </button>
          )}

          {error && (
            <div style={errorBoxStyle}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {view && (
            <div style={{ marginTop: '1.25rem' }}>
              {result && (
                <div style={successBannerStyle}>
                  <Check size={18} /> Import terminé — {result.summary.created} créé(s)
                  {view.enrichExisting ? `, ${result.summary.enriched} complété(s)` : ''},{' '}
                  {result.summary.managersLinked} rattachement(s) hiérarchique(s).
                </div>
              )}

              <div style={view.enrichExisting ? statsGrid4Style : statsGridStyle}>
                <Stat label="Lignes CSV" value={view.summary.csvRows} />
                <Stat
                  label={result ? 'Créés' : 'À créer'}
                  value={result ? view.summary.created : view.summary.toCreate}
                  accent="var(--primary)"
                />
                {view.enrichExisting && (
                  <Stat
                    label={result ? 'Complétés' : 'À compléter'}
                    value={result ? view.summary.enriched : view.summary.toEnrich}
                    accent="var(--accent)"
                  />
                )}
                <Stat label="Déjà présents" value={view.summary.skippedExisting} accent="var(--text-muted)" />
              </div>

              {view.toCreate?.length > 0 && (
                <Section
                  title={
                    result
                      ? `Créés (${view.toCreate.length})`
                      : `À créer (${selCreate.size}/${view.toCreate.length})`
                  }
                  action={
                    !result && (
                      <SelectAll
                        all={selCreate.size === view.toCreate.length}
                        onAll={() => setSelCreate(new Set(view.toCreate.map((p) => p.key)))}
                        onNone={() => setSelCreate(new Set())}
                      />
                    )
                  }
                >
                  <ul style={listStyle}>
                    {view.toCreate.map((p, i) => (
                      <li key={i} style={liStyle}>
                        {!result && (
                          <input
                            type="checkbox"
                            checked={selCreate.has(p.key)}
                            onChange={() => toggleCreate(p.key)}
                            style={{ flexShrink: 0, cursor: 'pointer' }}
                          />
                        )}
                        <UserPlus size={13} style={{ color: 'var(--success)', flexShrink: 0 }} />
                        <span>
                          <strong>
                            {p.lastName} {p.firstName}
                          </strong>{' '}
                          {p.smileId && <span style={muted}>[{p.smileId}]</span>} {p.jobTitle && `· ${p.jobTitle}`}{' '}
                          {p.bu && <span style={muted}>· {p.bu}</span>}
                          {p.archived && <span style={{ ...muted, color: 'var(--warning)' }}> · archivé</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {view.toEnrich?.length > 0 && (
                <Section
                  title={
                    result
                      ? `Complétés (${view.toEnrich.length})`
                      : `À compléter (${selEnrich.size}/${view.toEnrich.length})`
                  }
                  action={
                    !result && (
                      <SelectAll
                        all={selEnrich.size === view.toEnrich.length}
                        onAll={() => setSelEnrich(new Set(view.toEnrich.map((e) => e.personId)))}
                        onNone={() => setSelEnrich(new Set())}
                      />
                    )
                  }
                >
                  <ul style={listStyle}>
                    {view.toEnrich.map((e, i) => (
                      <li key={i} style={liStyle}>
                        {!result && (
                          <input
                            type="checkbox"
                            checked={selEnrich.has(e.personId)}
                            onChange={() => toggleEnrich(e.personId)}
                            style={{ flexShrink: 0, cursor: 'pointer' }}
                          />
                        )}
                        <Sparkles size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                        <span>
                          <strong>{e.label}</strong>{' '}
                          <span style={muted}>
                            · {e.fills.map((f) => `${PENTA_LABELS[f.field] ?? f.field} = ${f.value}`).join(', ')}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {result?.managersUnresolved?.length > 0 && (
                <Section title={`Responsables non résolus (${result.managersUnresolved.length})`}>
                  <ul style={listStyle}>
                    {result.managersUnresolved.map((m, i) => (
                      <li key={i} style={{ ...liStyle, color: 'var(--text-muted)' }}>
                        {m.person} → responsable smileId {m.responsableSmileId} introuvable
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {view.skipped?.length > 0 && (
                <Section title={`Ignorés (${view.skipped.length})`}>
                  <ul style={listStyle}>
                    {view.skipped.map((s, i) => (
                      <li key={i} style={{ ...liStyle, color: 'var(--text-muted)' }}>
                        {s.label} — {SKIP_LABELS[s.reason] ?? s.reason}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondary}>
              {result ? 'Terminer' : 'Annuler'}
            </button>
            {!result && !plan && (
              <button onClick={preview} disabled={!csv || busy} style={btnPrimary}>
                <Upload size={16} />
                {busy ? 'Analyse…' : 'Prévisualiser'}
              </button>
            )}
            {!result && plan && (
              <button
                onClick={apply}
                disabled={busy || (selCreate.size === 0 && selEnrich.size === 0)}
                style={btnPrimary}
              >
                <Check size={16} />
                {busy
                  ? 'Import…'
                  : plan.enrichExisting
                    ? `Appliquer (${selCreate.size} créé(s), ${selEnrich.size} complété(s))`
                    : `Créer ${selCreate.size} collaborateur(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Section({ title, children, action }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.4rem',
        }}
      >
        <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Boutons « Tout / Aucun » d'une section sélectionnable. */
function SelectAll({ all, onAll, onNone }) {
  return (
    <span style={{ display: 'inline-flex', gap: '0.5rem' }}>
      <button type="button" onClick={onAll} disabled={all} style={linkBtn}>
        Tout
      </button>
      <button type="button" onClick={onNone} style={linkBtn}>
        Aucun
      </button>
    </span>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      style={{
        padding: '0.75rem',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--glass)',
      }}
    >
      <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600, color: accent || 'var(--text)' }}>{value}</div>
    </div>
  );
}

const muted = { color: 'var(--text-muted)', fontSize: '0.85rem' };
const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: 'var(--card-bg)', borderRadius: 'var(--radius-md)',
  width: '90%', maxWidth: 640, maxHeight: '85vh', overflow: 'auto',
  border: '1px solid var(--glass-border)',
};
const headerStyle = {
  padding: '1rem 1.25rem', borderBottom: '1px solid var(--glass-border)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '0.3rem', border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--text-muted)',
};
const dropZoneStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  padding: '2rem', border: '2px dashed var(--glass-border)',
  borderRadius: 'var(--radius-md)', cursor: 'pointer', textAlign: 'center',
};
const errorBoxStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.6rem 0.8rem', marginTop: '1rem',
  background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)',
  borderRadius: 'var(--radius-sm)', fontSize: '0.85rem',
};
const successBannerStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.6rem 0.8rem',
  background: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)',
  borderRadius: 'var(--radius-sm)', fontSize: '0.9rem', marginBottom: '1rem',
};
const statsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' };
const statsGrid4Style = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' };
const linkBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: 'var(--accent)', fontSize: '0.8rem', fontWeight: 500,
};
const pillStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.4rem 0.9rem', borderRadius: 999, border: '1px solid var(--glass-border)',
  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
};
const listStyle = { margin: 0, paddingLeft: 0, listStyle: 'none', maxHeight: 200, overflow: 'auto' };
const liStyle = {
  display: 'flex', alignItems: 'center', gap: '0.4rem',
  fontSize: '0.85rem', padding: '0.15rem 0',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 1rem', background: 'var(--primary)', color: '#fff',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
  fontSize: '0.9rem', fontWeight: 500,
};
const btnSecondary = {
  padding: '0.5rem 1rem', background: 'var(--glass)', color: 'var(--text)',
  border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)',
  cursor: 'pointer', fontSize: '0.9rem',
};
